/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Grid,
  LinearProgress,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
} from '@material-ui/core';
import { useNavigate, useParams } from 'react-router';
import { useStyles } from '../../../styles';
import BackButton from '../../../../utils/backButton';
import { HelpComponent } from '../../../../utils/helpComponent';
import HELP_URL from '../../../../utils/helpLinkConstant';
import ManageConfigTable from './components/ManageConfigTable';
import Refresh from '@material-ui/icons/Refresh';
import Add from '@material-ui/icons/Add';
import { usePTOperations } from './hooks/usePTOperations';
import EmptyConfigBox from './components/EmptyConfigBox';
import { ITPContext, TPContext } from '../../../providers/TPProvider';
import { useFilteredConfigs } from './hooks/useFilteredConfigs';
import ConfigPopup from './components/ConfigPopup';
import Search from '@material-ui/icons/Search';

const ManageConfigPage = ({ isToolAdmin }: { isToolAdmin: boolean }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [filterOption, setFilterOption] = useState({
    status: 'All status',
  });
  const [statusType, setStatusType] = useState<any>([]);

  const { projectId } = useParams();
  const {
    configs,
    loading,
    runConfig,
    validateConfig,
    deleteConfig,
    duplicateConfig,
    refreshTime,
    refreshConfigs,
  } = usePTOperations(projectId!, true);

  const [sort, setSort] = useState('descending');
  const [searchText, setSearchText] = useState('');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const { filteredConfigs } = useFilteredConfigs(
    configs,
    filterOption.status,
    searchText,
    sort,
  );

  const emptyPopupState = {
    open: false,
    onClose: () => {},
    popupContent: {
      message: '',
      title: '',
      type: '',
    },
    onConfirm: () => {},
  };

  const [confirmPopup, setConfirmPopup] = useState({
    ...emptyPopupState,
  });

  const handleCloseConfirmationPopup = () => {
    setConfirmPopup(prevState => {
      return {
        ...prevState,
        open: false,
      };
    });
  };

  const handleSubmitConfirmPopup = (configId: string, validate?: boolean) => {
    setConfirmPopup(prevState => {
      return {
        ...prevState,
        open: false,
      };
    });
    if (validate) {
      validateConfig(configId).then(() => refreshConfigs());
    } else {
      runConfig(configId).then(() => refreshConfigs());
    }
  };

  const handleConfirmationPopup = (configId: string) => {
    setConfirmPopup({
      open: true,
      onClose: () => {
        handleCloseConfirmationPopup();
      },
      popupContent: {
        message: 'Are you sure you want to run this config?',
        title: 'Confirmation',
        type: 'confirmRun',
      },
      onConfirm: () => {
        handleSubmitConfirmPopup(configId);
      },
    });
  };

  const handleValidateConfirmPopup = (configId: string) => {
    setConfirmPopup({
      open: true,
      onClose: () => {
        handleCloseConfirmationPopup();
      },
      popupContent: {
        message:
          'You can run a config only if it is valid. Please validate the config first.',
        title: 'Validate config',
        type: 'validateConfig',
      },
      onConfirm: () => {
        handleSubmitConfirmPopup(configId, true);
      },
    });
  };

  useEffect(() => {
    if (configs && configs.length > 0) {
      setStatusType([
        'All status',
        ...new Set(configs.map((config: any) => config.status)),
      ]);
    }
  }, [configs]);

  const navigateToAddConfig = () => {
    navigate('./add-config');
  };

  // For Flow availble related check
  const { apmInfo }: ITPContext = useContext(TPContext);

  return (
    <>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          className={classes.flexSpacebetween}
        >
          <BackButton />
          <HelpComponent
            helpUrl={HELP_URL.PERFORMANCE_TEST_MANAGECONFIG_PAGE}
          />
        </Grid>
      </Grid>

      <Grid container className={classes.pageContainerPadding}>
        <Grid item xs={12} sm={12} md={2} lg={2}>
          <Typography variant="h5">Manage Config</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={10}
          lg={10}
          className={`${classes.manageConfigHeaderButtons}`}
        >
          <div className={classes.runsPageButtonGroup}>
            <Button
              variant="contained"
              size="medium"
              disabled={!apmInfo?.flows || configs?.length === 0}
              className={`${classes.width50PercentMobile} ${classes.refreshButton} ${classes.padding1} ${classes.minWidthAuto}`}
              onClick={() => refreshConfigs()}
            >
              <Refresh />
            </Button>
            <FormControl className={classes.width50PercentMobile}>
              <InputLabel shrink variant="outlined">
                Status
              </InputLabel>
              <Select
                className={classes.statusDropdown}
                label="Status"
                id="feedback-register-types"
                multiline
                onChange={(val: any) => {
                  setFilterOption({
                    ...filterOption,
                    status: val.target.value,
                  });
                }}
                disabled={!apmInfo?.flows || statusType.length === 0}
                value={filterOption.status}
                variant="outlined"
              >
                {statusType &&
                  statusType.map((t: string) => (
                    <MenuItem key={`type-option-${t}`} value={t}>
                      {t}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
          <TextField
            variant="outlined"
            type="search"
            className={classes.searchInRunsPage}
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Search configs"
            InputProps={{ endAdornment: !searchText && <Search /> }}
          />
          {isToolAdmin ? (
            <Button
              variant="contained"
              color="primary"
              className={classes.manageConfigHeaderActionButton}
              onClick={navigateToAddConfig}
              disabled={!apmInfo?.flows}
            >
              <Add />
              Add Config
            </Button>
          ) : null}
        </Grid>
      </Grid>
      <Grid container className={classes.emptyBoxContainer} spacing={0}>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {loading && <LinearProgress />}
          {configs?.length === 0 ? (
            !loading && (
              <EmptyConfigBox
                navigatePath="./add-config"
                flows={apmInfo?.flows}
                isToolAdmin={isToolAdmin}
                text="configs"
              />
            )
          ) : (
            <ManageConfigTable
              isToolAdmin={isToolAdmin}
              refreshTime={refreshTime}
              onRun={(configId: string) => handleConfirmationPopup(configId)}
              onValidate={(configId: string) =>
                handleValidateConfirmPopup(configId)
              }
              onEdit={(id: string) => {
                navigate(`./edit-config/${id}`, {
                  state: {
                    ...configs.find(
                      (config: any) => config.devxConfigId === id,
                    ),
                  },
                });
              }}
              onDuplicate={(config: any) => {
                duplicateConfig(config).then(() => refreshConfigs());
              }}
              onDelete={(configId: string) => {
                deleteConfig(configId);
              }}
              configData={filteredConfigs}
              sort={sort}
              onSort={setSort}
              onConfigClick={(configName: string) => {
                navigate(`../../runs`, {
                  state: {
                    configName,
                  },
                });
              }}
            />
          )}
        </Grid>
      </Grid>
      {confirmPopup?.open && <ConfigPopup {...confirmPopup} />}
    </>
  );
};

export default ManageConfigPage;
